import type { FlexProps } from 'antd';
import { FC, HTMLAttributes, memo, ReactNode, useState } from 'react';
import { TableRow } from '../common';
import { useTableStoreValue } from '../TableContext';
import { WithFilterOptions, WithFilterOptionsProps } from './WithFilterOptions';
import { WithStickyHeaderIfApplicable } from './WithStickyHeaderIfApplicable';
import { TableShell } from './TableShell';
import { WithActions } from './WithActions';
import { WithRowDragTargetCrosshair } from '../../form/table/tanstack/WithRowDragTargetCrosshair';
import { Row, Table as TableInstance } from '@tanstack/react-table';
import { NoDataRow } from '../common/NoDataRow';
import { TableSpinner } from '../common/TableSpinner';

export interface DefaultRendererProps extends WithFilterOptionsProps {
  actions?: ReactNode | ((table: TableInstance<any>) => ReactNode);
  actionsInFront?: boolean;
  actionsWrapperProps?: FlexProps;
  scrollContainer?: HTMLElement;
  hideClearFilters?: boolean;
  hideEmptyImage?: boolean;
  rowHtmlAttributes?: (
    row: any,
    rowIndex: number,
  ) => HTMLAttributes<HTMLTableRowElement> | undefined;
  isLoading?: boolean;
  className?: string;
  renderBody?: (rows: Row<any>[]) => ReactNode;
}

export const DefaultRenderer: FC<DefaultRendererProps> = memo((props) => {
  const {
    actions,
    actionsInFront,
    actionsWrapperProps,
    scrollContainer,
    hideClearFilters,
    hideEmptyImage,
    rowHtmlAttributes,
    isLoading,
    className,
    renderBody,
  } = props;
  const [table] = useTableStoreValue('table');
  const { rows } = table.getRowModel();
  const colSpan = table.getAllColumns().length;
  const [, setContainer] = useState<HTMLDivElement | null>(null);

  return (
    <WithActions
      actions={actions}
      actionsInFront={actionsInFront}
      actionsWrapperProps={actionsWrapperProps}
      scrollContainer={scrollContainer}
      hideClearFilters={hideClearFilters}
    >
      {isLoading && <TableSpinner />}
      <WithStickyHeaderIfApplicable>
        <WithFilterOptions {...props}>
          <WithRowDragTargetCrosshair containerRef={setContainer}>
            <TableShell className={className}>
              {renderBody
                ? renderBody(rows)
                : rows.map((row) => (
                    <TableRow
                      index={row.index}
                      row={row}
                      key={row.id}
                      htmlAttributes={rowHtmlAttributes?.(
                        row.original,
                        row.index,
                      )}
                    />
                  ))}
              {rows.length === 0 && !isLoading && (
                <NoDataRow colSpan={colSpan} hideImage={hideEmptyImage} />
              )}
            </TableShell>
          </WithRowDragTargetCrosshair>
        </WithFilterOptions>
      </WithStickyHeaderIfApplicable>
    </WithActions>
  );
});
